import { useMemo, type ReactElement, type ReactNode } from 'react';
import { VirtuosoGrid } from 'react-virtuoso';
import { useInfiniteQuery } from '@tanstack/react-query';
import type { IWebCOOSAssetSummaryView, IWebCOOSElement, IWebCOOSElementPagedResult, IWebCOOSParsedGalleryService } from '@/services/assets/types';
import { useAPIContext } from '@/state/ApiContext';
import { fetchTimeseriesAssetMedia } from '@/services/assets/services';
import MediaThumb from '@/Components/Media/MediaThumb';
import { Loader } from '@axdspub/axiom-ui-utilities';


const addMs = (date: Date, ms: number) => new Date(date.getTime() + ms);

export const InfiniteScrollList = (
    {
        start,
        end,
        elementCount,
        pageSize = 20,
        service,
        asset,
        ItemContainer = ({ children }: {
            element: IWebCOOSElement,
            children: ReactNode
        }): ReactElement => {
            return (
                <>{children}</>
            )
        }
    }: {
        start: Date,
        end: Date,
        elementCount?: number,
        pageSize?: number,
        service: IWebCOOSParsedGalleryService,
        asset: IWebCOOSAssetSummaryView,
        ItemContainer?: React.FC<{ element: IWebCOOSElement, children: ReactNode }>

    }
): ReactElement => {

    const apiContext = useAPIContext()


    // Fetch function that uses date range and pageSize
    const fetchElementsByDate = async ({
        pageParam,
        signal
    }: {
        pageParam?: number;
        signal?: AbortSignal;
    }): Promise<IWebCOOSElementPagedResult> => {

        const r = await fetchTimeseriesAssetMedia({
            ...apiContext,
            serviceIdentifier: service.uuid,
            start: addMs(start, -1),
            end: addMs(end, 1), // Add 1 ms to avoid overlap
            count: pageSize,
            page: pageParam,
            signal,
            nameOrSlugPrefix: service.common.label.includes('with-shoreline') ? 'annotated.shoreline' : undefined
        })
        return r
    };

    const {
        data,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        status,
        error
    } = useInfiniteQuery({
        queryKey: ['elements', service.uuid, asset.asset_slug, start, end, elementCount, pageSize],
        queryFn: async ({ pageParam = 1, signal }: { pageParam: number | undefined, signal: AbortSignal }) => {
            const elements = await fetchElementsByDate({ pageParam, signal });
            return elements;
        },
        initialPageParam: 1,
        getNextPageParam: (lastPage) => {
            const nextPage = lastPage?.pagination?.next_page !== null ? +lastPage.pagination.next_page : undefined;
            return nextPage ?? null
        },
    });

    // Flatten paginated data
    const allElements = useMemo(() =>
        (data?.pages?.map(page => page.results)?.flat() ?? []),
        [data]
    );

    if (status === 'error') return <div>Error: {(error as Error).message}</div>;

    return (
        <>
            <VirtuosoGrid
                useWindowScroll={true}
                data={allElements}
                endReached={() => {
                    if (hasNextPage && !isFetchingNextPage) {
                        fetchNextPage();
                    }
                }}
                listClassName="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 p-4"
                components={{
                    Item: ({ children, ...props }) => {
                        return (
                            <div {...props} className="flex flex-col aspect-5/3">
                                {children}
                            </div>
                        )
                    },
                    /* Footer: () => {
                        return <>{
                            !endReached && <div className='p-10 text-center relative bg-slate-200'><em>Loading..</em></div>
                        }</>
                    } */
                }}
                itemContent={(_index, element: IWebCOOSElement) => (
                    <ItemContainer element={element}>
                        <MediaThumb
                            className='bg-slate-200 my-4 aspect-video'
                            asset={asset}
                            element={element}
                            serviceSlug={service.common.slug}
                        />
                    </ItemContainer>
                )}
            />
            {
                hasNextPage && <div className='m-10 text-center relative'>
                    <Loader className='pt-10' />
                </div>
            }
        </>
    );
};